import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  Tab,
  Tabs,
  Typography,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  GetApp,
  Close as CloseIcon,
} from "@mui/icons-material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import Button from "../../common/Button";

import { columns } from "./columnsUser";
import ListTable from "../../common/Table";
import UserModal from "./AddUserModal";
import OrganisatioFilter from "../Statisctics/OrganisationFilter";
import UploadUsersModal from "./UploadUsers";
import ManageGroups from "./ManageGroups";

import {
  addUserByAdmin,
  deleteUsers,
  editUserByAdmin,
  getAllUsersByAdmin,
} from "../../../store/api/users";
import { roles } from "../../../constants/routes";
import { getRole } from "../../../helpers/auth";
import { useOrganisation } from "../../../hooks/useOrganisation";
import CommonSearch from "../../common/Search";

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-management-tabpanel-${index}`}
      aria-labelledby={`user-management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Users = () => {
  const queryClient = useQueryClient();
  const role = getRole();
  const { primaryColor } = useOrganisation();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("DateCreated");
  const [selectedIds, setSelectedIds] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [isDescending, setIsDescending] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [IdUserToEdit, setIdUserToEdit] = useState("");
  const [isModalCSVOpen, setIsModalCSVOpen] = useState(false);
  const [organisation, setOrganisation] = useState(null);

  // Group filter state
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState("");

  // Add state for modal error handling
  const [modalError, setModalError] = useState("");

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const {
    data,
    isLoading,
    isLoading: isInitialLoading,
  } = useQuery({
    queryKey: [
      "adminUsers",
      searchTerm,
      currentPage,
      sortBy,
      isDescending,
      organisation,
      selectedGroupId,
      pageSize,
    ],
    queryFn: () =>
      getAllUsersByAdmin({
        searchTerm,
        currentPage,
        sortBy,
        isDescending,
        organisationId: organisation?.id,
        groupId: selectedGroupId,
        pageSize,
      }),
    keepPreviousData: true,
    // Only enable query when on Users tab
    enabled: tabValue === 0,
  });

  const { mutate: addMutate, isPending: isAddLoading } = useMutation({
    mutationFn: (user) =>
      addUserByAdmin({
        fullName: user.fullName,
        email: user.email,
        locationId: user.locations,
        userTypeId: user.userTypes,
        organisationId: user.organisationId,
        groupIds: user.groupIds || [],
      }),
    onSuccess: () => {
      queryClient.refetchQueries(["adminUsers"]);
      setToast({
        open: true,
        message: "User added successfully",
        severity: "success",
      });
      setIsAddModalOpen(false);
      setModalError(""); // Clear any previous errors
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.title ||
        "An error occurred while adding the user";
      setModalError(errorMessage); // Set error for modal display
    },
  });

  const { mutate: editMutate, isPending: isEditLoading } = useMutation({
    mutationFn: (user) =>
      editUserByAdmin({
        id: IdUserToEdit,
        fullName: user.fullName,
        email: user.email,
        locationId: user.locations,
        userTypeId: user.userTypes,
        groupIds: user.groupIds || [],
      }),
    onSuccess: () => {
      queryClient.refetchQueries(["adminUsers"]);
      setToast({
        open: true,
        message: "User updated successfully",
        severity: "success",
      });
      setIsEditMode(false);
      setIdUserToEdit("");
      setIsAddModalOpen(false);
      setModalError(""); // Clear any previous errors
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.title ||
        "An error occurred while editing the user";
      setModalError(errorMessage); // Set error for modal display
    },
  });

  const { mutate: deleteMutate } = useMutation({
    mutationFn: () => deleteUsers([...selectedIds]),
    onSuccess: () => {
      setToast({
        open: true,
        message: "Users deleted successfully",
        severity: "success",
      });
      queryClient.refetchQueries(["adminUsers"]);
      setSelectedIds([]);
      if (allUsers.length === selectedIds.length && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    },
    onError: (error) => {
      setToast({
        open: true,
        message: "Error deleting users",
        severity: "error",
      });
    },
  });

  useEffect(() => {
    if (!isLoading) {
      setAllUsers(data?.paginatedContent || []);
      setTotalPage(data?.totalPages || 1);
    }
  }, [isLoading, data]);

  // Listen for redirections from the ManageGroups component
  useEffect(() => {
    const handleRedirectToUserTab = async (event) => {
      const { groupId, groupName } = event.detail;

      // Switch to Users tab first
      setTabValue(0);

      // Set group filter
      setSelectedGroupId(groupId);
      setSelectedGroupName(groupName);

      // Reset pagination
      setCurrentPage(1);
    };

    window.addEventListener("redirectToUserTab", handleRedirectToUserTab);

    return () => {
      window.removeEventListener("redirectToUserTab", handleRedirectToUserTab);
    };
  }, []);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (term !== searchTerm) {
      setCurrentPage(1);
    }
  };

  const handleClearGroupFilter = () => {
    setSelectedGroupId(null);
    setSelectedGroupName("");
    setCurrentPage(1);
  };

  const handleOpenModal = () => {
    setIsEditMode(false);
    setModalError(""); // Clear any previous errors
    setIsAddModalOpen(true);
  };

  const handleEditUser = (id) => {
    setIdUserToEdit(id);
    setIsEditMode(true);
    setModalError(""); // Clear any previous errors
    setIsAddModalOpen(true);
  };

  const handleSubmitUser = (user) => {
    setModalError(""); // Clear any previous errors
    if (isEditMode) {
      editMutate({ ...user });
    } else {
      addMutate({ ...user });
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === allUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allUsers.map((user) => user.id));
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId) => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };

  const handleDelete = () => {
    deleteMutate();
    setOpenDelete(false);
  };

  const handleOpenDelete = () => {
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleOpenCSVModal = () => {
    setIsModalCSVOpen(true);
  };

  const handleCloseCSVModal = () => {
    setIsModalCSVOpen(false);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setModalError(""); // Clear errors when closing
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);

    // Clear group filter when switching away from Users tab
    if (newValue !== 0) {
      setSelectedGroupId(null);
      setSelectedGroupName("");
    }
  };

  // Fixed sorting handler - now properly receives both parameters
  const handleSortChange = (newSortBy, newIsDescending) => {
    console.log("Sort change:", newSortBy, newIsDescending);
    setSortBy(newSortBy);
    setIsDescending(newIsDescending);
    setCurrentPage(1);
  };

  const handleOrganisationChange = (newOrganisation) => {
    setOrganisation(newOrganisation);
    setCurrentPage(1);
  };

  return (
    <div>
      <Typography variant="h6" fontWeight={"bold"}>
        User Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="user management tabs"
          sx={{
            "& .MuiTab-root.Mui-selected": {
              color: primaryColor,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: primaryColor,
            },
          }}
        >
          <Tab label="Manage Users" />
          <Tab label="Manage Groups" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box>
          {selectedGroupId && (
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1">
                Showing users in group:
              </Typography>
              <Chip
                label={selectedGroupName}
                color="primary"
                onDelete={handleClearGroupFilter}
                deleteIcon={<CloseIcon />}
              />
            </Box>
          )}

          <Grid container flex={1} justifyContent={"space-between"}>
            <Box
              sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}
            >
              <CommonSearch
                placeholder="Search users"
                onSearch={handleSearch}
                initialValue={searchTerm}
                sx={{ flexGrow: 1, marginRight: 2 }}
              />
              <Button
                endIcon={<ControlPointIcon />}
                onClick={handleOpenModal}
                text={"Add User"}
              />

              <Button
                endIcon={<GetApp />}
                onClick={handleOpenCSVModal}
                text={"Upload CSV"}
                backgroundColor="white"
              />

              <UploadUsersModal
                open={isModalCSVOpen}
                onClose={handleCloseCSVModal}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <Button
                variant="outlined"
                endIcon={<DeleteIcon />}
                onClick={handleOpenDelete}
                disabled={selectedIds.length === 0}
                text="Delete user"
                backgroundColor="white"
              />
            </Box>
            {roles.SuperAdmin === role && (
              <OrganisatioFilter setOrganisation={handleOrganisationChange} />
            )}
          </Grid>
        </Box>
        <ListTable
          data={allUsers}
          columns={columns({
            setSortBy: handleSortChange,
            sortBy,
            isDescending,
            handleEditUser,
            handleSelectAll,
            selectedIds,
            handleSelect,
          })}
          isLoading={isLoading}
          isInitialLoading={isInitialLoading}
          totalPage={totalPage}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          showPageSize={true}
        />

        <UserModal
          open={isAddModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitUser}
          isEdit={isEditMode}
          IdUserToEdit={IdUserToEdit}
          role={role}
          isSubmitting={isEditMode ? isEditLoading : isAddLoading} // Pass loading state
          submitError={modalError} // Pass error state
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ManageGroups />
      </TabPanel>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure you want to delete the selected users?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Deleting these users cannot be undone. Please confirm if you want to
            proceed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDelete}
            backgroundColor="white"
            text="Cancel"
          />

          <Button onClick={handleDelete} text="Delete" />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;
